import { useItemsStore } from '@/stores/Items.store';
import get from 'lodash.get';

export default function itemsServiceFactory(services) {
  const thisService = {
    init,
    setOptions,
    getOptions,
    getItemsAsync,
    getItems,
    setItems,
    getItemAsync,
    getItem,
    setItem,
    getParams: _getParams,
    getPath: _getPath,
  };

  let itemStore;
  return thisService;

  //////////////

  function init() {
    itemStore = useItemsStore();
  }

  function setOptions(options) {
    if (!options.id) {
      console.warn('Item Service: Invalid Options - Require id property');
      return;
    }

    if (!options.items) {
      console.warn('Item Service: Invalid Options - Require items property');
      return;
    }

    let oldItemsCache = itemStore.options[options.id]?.items?.cache ? {...itemStore.options[options.id].items.cache} : null;
    let oldItemCache = itemStore.options[options.id]?.item?.cache ? {...itemStore.options[options.id].item.cache} : null;
    if (itemStore.hasOptions[options.id]) {
      options = {...itemStore.options[options.id], ...options};
      if (options.items)
        if (options.debug) { console.log('Item Service: Options already set, merging', options)}
    }

    if (options.items) {
      if (!options.items.service || !options.items.api) {
        console.warn('Item Service: Invalid Items Configuration- Require service and api properties');
        return;
      }

      if (!options.items.type) {
        console.warn('Item Service: Items Configuration - No type specified (flat/paged/indexed)');
        return;
      }

      if (options.items.type === 'indexed' && !options.items.startId) {
        console.warn('Item Service: No startId configured for type=indexed', options.id);
        return Promise.reject(`No startId configured in items for type=indexed`);
      }

      options.items._expectedParams = _getParams(options.items.api);
      options.items.cache = options.items.cache || {};
      options.items.cache._lastUpdates = oldItemsCache ? oldItemsCache._lastUpdates : {};
      options.items.cache.partial = oldItemsCache ? oldItemsCache.partial : false;
      options.items.cache.pages = oldItemsCache ? oldItemsCache.pages : 0;
      options.items.cache.total = oldItemsCache ? oldItemsCache.total : 0;
      options.items.cache.retrievedItems = oldItemsCache ? oldItemsCache.retrievedItems : 0;
      options.items.cache.retrievedPages = oldItemsCache ? oldItemsCache.retrievedPages : 0;
    }

    if (options.item) {
      if (!options.item.service || !options.item.api) {
        console.warn('Item Service: Invalid Item Config - Need service and api');
        return;
      }

      if (!options.idPath) {
        console.warn('Item Service: Invalid Item Config - Need idPath to update items');
      }

      options.item._expectedParams = _getParams(options.item.api);
      options.item.cache = options.item.cache || {};
      options.item.cache._lastUpdates = oldItemCache ? oldItemCache._lastUpdates : {};
    }

    itemStore.setOptions(options);
  }

  function getOptions(optionsId) {
    return itemStore.options[optionsId] ? itemStore.options[optionsId] : null;
  }

  function _isValidItemsCache(options, cacheId) {
    if (!itemStore.hasItems(options.id, cacheId)) {
      if (options.debug) { console.log(`Item Service: Invalid cache (No stored items`) }
      return false;
    }

    if (options.items.pageLimit && options.items.cache.partial && options.items.pageLimit > options.items.cache.retrievedPages) {
      if (options.debug) { console.log(`Item Service: Invalid cache (Partial cache with new pageLimit`) }
      return false;
    }

    if (!options.items.pageLimit && options.items.cache.partial) {
      if (options.debug) { console.log(`Item Service: Invalid cache (Partial cache with no pageLimit`) }
      return false;
    }

    if (!options.items.cache.time) {
      if (options.debug) { console.log(`Item Service: Valid Cache`) }
      return true;
    }

    let lastUpdate = options.items.cache._lastUpdates[cacheId] ? options.items.cache._lastUpdates[cacheId] : 0;
    let validCacheTime = Date.now() - lastUpdate < options.items.cache.time;
    if (options.debug && !validCacheTime) { console.log(`Item Service: Invalid cache (Cache timeout`) }
    return validCacheTime;

  }

  function getItemsAsync(optionsId, refresh = false, pathParams , config) {
    let debugStart = Date.now();
    let myPathParams = pathParams ? pathParams : {};
    let options = itemStore.options[optionsId];
    if (!options) {
      console.warn('Item Service: No options configured for id', optionsId);
      return Promise.reject(`No options for id (${optionsId}).  Use setOptions(id, options) to configure Item service for your items`);
    }

    if (options.items.cache.cacheId && !myPathParams[options.items.cache.cacheId]) {
      console.warn('Item Service: Could not resolve cacheId, will fall back to options.id for caching');
    }

    let cacheId = options.items.cache.cacheId && myPathParams[options.items.cache.cacheId] ? myPathParams[options.items.cache.cacheId] : options.id;
    if (options.debug) { console.log('Item Service: CacheId', options.id, cacheId)}
    if (!refresh && _isValidItemsCache(options, cacheId)) {
      return Promise.resolve(itemStore.getItems(optionsId, cacheId));
    }

    if (!_checkParams(options.items._expectedParams, myPathParams)) {
      return Promise.resolve([]);
    }


    let myPath = _getPath(options.items.api, options.items._expectedParams, myPathParams);
    let myConfig = options.items.config || {};
    if (config) {
      myConfig = {...myConfig, ...config};
    }

    let myPromise = null;
    switch(options.items.type) {
      case 'flat':
        myPromise = _getFlatItemsAsync(options.items.service, myPath, myConfig, options);
        break;
      case 'paged':
        myPromise = _getPagedItemsAsync(options.items.service, myPath, myConfig, options);
        break;
      case 'indexed':
        myPromise = _getIndexedItemsAsync(options.items.service, myPath, myConfig, options);
        break;
    }

    if (!myPromise) {
      return Promise.resolve([]);
    }

    return myPromise.then(items => {
      if (options.callbacks?.item) {
        items.forEach(item => {
          options.callbacks.item(item);
        });
      }

      itemStore.setItems(optionsId, cacheId, items);
      options.items.cache._lastUpdates[cacheId] = Date.now();
      itemStore.setOptions(options);

      if (options.debug) { console.log(`Item Service: Request Complete`, Date.now() - debugStart) }
      return items;
    });
  }

  function _getFlatItemsAsync(service, path, config, options) {
    let myConfig = services.helpers.copyObject(config);
    return services.http.get(service, path, myConfig).then(resp => {
      if (options.callbacks?.items) {
        options.callbacks.items(resp);
      }

      let items = options.items.dataPath ? get(resp, options.items.dataPath) : resp.data;
      if (!items || !Array.isArray(items)) {
        console.warn('Item Service: Could not find items in response', resp);
        return [];
      }

      return items;
    });
  }

  async function _getPagedItemsAsync(service, path, config, options) {
    let myConfig = services.helpers.copyObject(config);
    let limit = options.items.limit ? options.items.limit : 1000;
    let offset = 0;

    let items = [];
    let pageLimit = options.items.pageLimit ? options.items.pageLimit : 0;
    let total = 0;
    let pages = 1;
    let pageCount = 1;
    myConfig.params = myConfig.params ? {...myConfig.params, ...{ offset, limit }} : { offset, limit };
    if (options.debug) { console.log(`Item Service: Paged Items (Initial fetch)`, service, path, myConfig) }
    await services.http.get(service, path, myConfig).then(async resp => {
      if (options.callbacks?.items) {
        options.callbacks.items(resp);
      }

      let myItems = options.items.dataPath ? get(resp, options.items.dataPath) : resp.data;
      if (!myItems || !Array.isArray(myItems)) {
        console.warn('Item Service: Could not find items in response', resp);
        return [];
      }

      items = items.concat(myItems);
      total = options.items.totalPath ?  get(resp, options.items.totalPath) : get(resp, 'data.total', null);
      pages = total ? Math.ceil(total / limit) : 0;
      if (options.debug) { console.log(`Item Service: Paged Items Total Items`, total) }
      if (options.debug) { console.log(`Item Service: Paged Items Total Pages`, pages) }
      let promises = [];
      for (let p = 2; p < pages + 1; p++) {
        if (pageLimit > 0 &&  p > pageLimit) {
          break;
        }
        pageCount++;
        myConfig = services.helpers.copyObject(config);
        myConfig.params = myConfig.params ? {...myConfig.params, ...{ offset: (p-1) * limit, limit }} : { offset: (p-1) * limit, limit };
        if (options.debug) { console.log(`Item Service: Paged Items (Page fetch)`, service, path, myConfig) }
        promises.push(services.http.get(service, path, myConfig).then(resp2 =>{
          if (options.callbacks?.items) {
            options.callbacks.items(resp2);
          }

          let pageItems = options.items.dataPath ? get(resp2, options.items.dataPath) : resp2.data;
          if (!pageItems || !Array.isArray(pageItems)) {
            console.warn('Item Service: Could not find items in response', resp2);
            return [];
          }

          items = items.concat(pageItems);
        }));
      }

      await Promise.all(promises);
    });

    options.items.cache.partial = items.length < total;
    options.items.cache.pages = pages;
    options.items.cache.total = total;
    options.items.cache.retrievedItems = items.length;
    options.items.cache.retrievedPages = pageCount;
    return Promise.resolve(items);
  }

  async function _getIndexedItemsAsync(service, path, config, options) {
    let myConfig = services.helpers.copyObject(config);
    let nextId = options.items.startId;
    let limit = options.items.limit ? options.items.limit : 1000;

    let items = [];
    let lastItems = [];
    while (nextId) {
      myConfig.params = myConfig.params ? {...myConfig.params, ...{ id: nextId, limit }} : { id: nextId, limit };
      await services.http.get(service, path, myConfig).then(resp => {
        if (options.callbacks?.items) {
          options.callbacks.items(resp);
        }

        lastItems = options.items.dataPath ? get(resp, options.items.dataPath) : resp.data;
        if (!lastItems || !Array.isArray(lastItems)) {
          console.warn('Item Service: Could not find items in response', resp);
          return [];
        }

        items = items.concat(lastItems);

        // Determine nextIndex
        let lastItem = lastItems.length > 0 ? lastItems[lastItems.length - 1] : null;
        nextId = lastItems.length < limit ? null : get(lastItem, options.idPath);
      });
    }

    return Promise.resolve(items);
  }

  function getItems(optionsId, pathParams) {
    let options = itemStore.options[optionsId];
    if (!options) {
      console.warn('Item Service: No options configured for id', optionsId);
      return [];
    }

    let myPathParams = pathParams ? pathParams : {};
    let cacheId = options.items.cache.cacheId && myPathParams[cacheId] ? myPathParams[cacheId] : options.id;
    return itemStore.getItems(optionsId, cacheId);
  }

  function setItems(optionsId, items, cacheId) {
    let options = itemStore.options[optionsId];
    if (!options) {
      console.warn('Item Service: No options configured for id', optionsId);
      return;
    }

    let myCacheId = cacheId ? cacheId : options.optionsId;
    itemStore.setItems(optionsId, myCacheId, items);
    options.items.cache._lastUpdates[myCacheId] = Date.now();
  }

  function getItemAsync(optionsId, itemId, refresh = false, pathParams, config) {
    let debugStart = Date.now();
    let myPathParams = pathParams ? pathParams : {};
    let options = itemStore.options[optionsId];
    if (!options) {
      console.warn('Item Service: No options configured for id', optionsId);
      return Promise.reject(`Item Service: No options configured for ${optionsId}`);
    }

    if (!options.item) {
      console.warn('Item Service: Item configuration not specified for id', optionsId);
      return Promise.reject(`Item Service: No item configuration for ${optionsId}`);
    }

    myPathParams.itemId = itemId;

    let cacheId = options.items.cache.cacheId && myPathParams[options.items.cache.cacheId] ? myPathParams[options.items.cache.cacheId] : options.id;
    if (options.debug) { console.log('Item Service: CacheId', options.id, cacheId)}
    if (!refresh) {
      let item = itemStore.getItem(optionsId, cacheId, itemId);
      if (item) {
        if (options.item?.cache?.time || options.items?.cache?.time) {
          let lastUpdate = options.item.cache._lastUpdates[itemId] ? options.item.cache._lastUpdates[itemId] : 0;
          let cacheTime = options.item?.cache?.time || options.items?.cache?.time;
          if (options.debug) {
            console.log(`Item Service: Checking Cache Expiration - cache for: ${cacheTime} ms, last update: ${lastUpdate}, time since last update: ${Date.now() - lastUpdate}`)
          }
          if (Date.now() - lastUpdate < cacheTime) {
            if (options.debug) {
              console.log(`Item Service: Request Complete From Cache (ms remaining: ${cacheTime - (Date.now() - lastUpdate)})`, Date.now() - debugStart)
            }
            return Promise.resolve(item);
          }
        } else {
          if (options.debug) {
            console.log(`Item Service: Request Complete From Cache`, Date.now() - debugStart)
          }
          return Promise.resolve(item);
        }
      }
    }

    let myPath = _getPath(options.item.api, options.item._expectedParams, myPathParams);
    let myConfig = options.item.config || {};
    if (config) {
      myConfig = {...myConfig, ...config};
    }

    return services.http.get(options.item.service, myPath, myConfig).then(resp => {
      let item = options.item.dataPath ? get(resp, options.item.dataPath) : resp.data;
      if (!item) {
        console.warn('Item Service: Could not find item in response', resp);
        return null;
      }


      if (options.callbacks?.item) {
        options.callbacks.item(item);
      }

      itemStore.setItem(options.id, cacheId, item);
      options.item.cache._lastUpdates[itemId] = Date.now();

      if (options.debug) { console.log(`Item Service: Request Complete`, Date.now() - debugStart) }
      return item;
    });
  }

  function getItem(optionsId, itemId) {
    let options = itemStore.options[optionsId];
    if (!options) {
      console.warn('Item Service: No options configured for id', optionsId);
      return null;
    }

    // if (!options.item) {
    //   console.warn('Item Service: Item configuration not specified for id', optionsId);
    //   return Promise.reject(`Item Service: No item configuration for ${optionsId}`);
    // }

    let myPathParams = { itemId };
    let cacheId = options.items.cache.cacheId && myPathParams[options.items.cache.cacheId] ? myPathParams[options.items.cache.cacheId] : options.id;
    if (options.debug) { console.log('Item Service: CacheId', options.id, cacheId)}

    return itemStore.getItem(optionsId, cacheId, itemId);
  }

  function setItem(optionsId, item, cacheId) {
    let options = itemStore.options[optionsId];
    if (!options) {
      console.warn('Item Service: No options configured for id', optionsId);
      return null;
    }

    let myCacheId = cacheId ? cacheId : options.id;

    return itemStore.setItem(optionsId, myCacheId, item);
  }

  function _checkParams(expectedParams, pathParams) {
    let valid = true;
    expectedParams.forEach(param => {
      if (!pathParams[param]) {
        console.warn('Item Service: Missing Path Param', param);
        valid = false;
      }
    });

    return valid;
  }

  function _getPath(path, expectedParams, pathParams) {
    let myPath = path;
    expectedParams.forEach(ep => {
      let regex = new RegExp('\\{' + ep + '}', 'g');
      myPath = myPath.replaceAll(regex, pathParams[ep]);
    });

    return myPath;
  }

  function _getParams(path) {
    let params = [];
    if (!path || path.trim() === '') {
      // Nothing to parse
      return params;
    }

    let index = path.indexOf('{');
    while (index !== -1) {
      let endIndex = path.indexOf('}', index);
      if (endIndex === -1) {
        // No matching end brace so we're done
        break;
      }

      params.push(path.substr(index+1, endIndex - index - 1));
      index = path.indexOf('{', endIndex);
    }

    return params;
  }
}
